<template lang="pug">
  v-dialog(v-model="show" max-width="500" :fullscreen="$vuetify.breakpoint.smAndDown" scrollable persistent)
    v-snackbar( v-model="snackBar" :color="snackBarColor" ) {{ snackBarMessage }}
      template( v-slot:action="{attrs}")
        v-btn(text v-bind="attrs"
          @click="snackBar = false") {{ $t('buttons.closeButtonLabel') }}
    WarningDialog(
      v-if="showWarningDialog"
      :showWarningDialog="showWarningDialog"
      :tagDetail="tagDetail"
      @yes="onDeleteButtonClick"
      @close="onCloseWarningDialog")
    v-card.d-flex.flex-column
      v-card-title.headline {{tagDetail ? $t('settingsPage.updateTag') : $t('settingsPage.addNewLabel')}}
      v-card-text.flex-shrink-0.flex-grow-1.pb-0
        v-form(@submit.prevent="onSubmitButtonClick"  v-model="isFormValid" lazy-validation :ref="form" :disabled="loading")
          v-row.ma-0
                v-col(cols="12")
                  v-text-field(outlined v-model="title" :label="$t('settingsPage.offerStructure.nameLabel')" :rules="titleRules" autofocus)
                v-col(cols="10")
                  v-text-field(outlined v-model="iconClass" clearable :label="$t('settingsPage.icon')" clear-icon="fas fa-times" @click:clear)
                v-col.text-end(cols="2")
                  v-icon.pt-3(medium) {{getIcon(iconClass)}}
      v-divider
      v-card-actions
        v-btn(v-if="tagDetail && tagDetail.id" color="error" :disabled="loading" @click="onDeleteTagClick") {{ $t('settingsPage.deleteButtonLabel') }}
        v-spacer
        v-btn( color="primary" text @click="onCancelButtonClick" :disabled="loading") {{ $t('optOutDialog.cancelButtonLabel') }}
        v-btn(color="primary" type="submit" @click="onSubmitButtonClick" :loading="loading" :disabled="!isFormValid") {{$t('optOutDialog.submitCreditButtonLabel') }}

</template>

<script lang="ts">

import {Component, Prop, Ref} from "vue-property-decorator";
import Vue from 'vue';
import i18n from "@/plugins/i18n";
import Tag from "@/model/tennant/Tag";
import {Action} from "vuex-class";
import {DELETE_SELECTED_TAG, SAVE_NEW_TAG, UPDATE_TAG} from "@/store/action-types";
import {NewTagPayload, UpdateTagPayload} from "@/types/payload";
import WarningDialog from "@/components/settings/WarningDialog.vue";

@Component({
  components: {
    WarningDialog
  }
})

export default class TagDetailDialog extends Vue {
  $refs!: {
    form: Vue & {
      validate(): boolean,
      resetValidation(): boolean
    }
  }

  @Prop({ type: Boolean, default: false }) readonly show!: boolean
  @Prop({ type: Object, required: false }) readonly tagDetail!: Tag
  @Action(UPDATE_TAG) updateTag!: ({ (payload: UpdateTagPayload): Promise<void> })
  @Action(SAVE_NEW_TAG) createNewTag!: ({ (payload: NewTagPayload): Promise<any> })
  @Action(DELETE_SELECTED_TAG) deleteSelectedTag!: ({ (payload: string): Promise<void> })
  @Ref('form') readonly form!: Vue & { validate(): boolean }

  private isFormValid: boolean = false
  private title: string = ''
  private titleRules: Array<(v:string) => string | boolean> = [
    (v:string) => !!v || i18n.t('settingsPage.offerStructure.nameValidationText').toString()
  ]
  private iconClass: string | null = ''
  private showWarningDialog = false
  private loading: boolean = false
  private snackBar: boolean = false
  private snackBarMessage: string = ''
  private snackBarColor: string = ''

  async onSubmitButtonClick ():Promise<void> {
      if(this.tagDetail && this.tagDetail.id) {
        const payload:UpdateTagPayload = {
          id: this.tagDetail!.id,
          title: this.title,
          iconClass: this.iconClass
        }
        try {
          this.loading = true
          await this.updateTag(payload)
          this.$emit('saved')
          this.loading = false

        }catch (e) {
          this.snackBar = true
          this.snackBarMessage = this.$t('settingsPage.snackbarErrorMessage').toString()
          this.snackBarColor = 'error'
        }
      }else {
        const payload: NewTagPayload = {
          title: this.title,
          iconClass: this.iconClass
        }
        try {
          this.loading = true
          await this.createNewTag(payload)
          this.loading = false
          this.$emit('create')
        }catch (e) {
          this.snackBar = true
          this.snackBarMessage = this.$t('settingsPage.snackbarErrorMessage').toString()
          this.snackBarColor = 'error'
        }
      }
  }

  onCloseWarningDialog () {
    this.showWarningDialog = false
  }

  onCancelButtonClick (): void {
    this.$emit('close')
  }

  onDeleteTagClick () {
    this.showWarningDialog = true
  }

  async onDeleteButtonClick ():Promise<void> {
    this.showWarningDialog = false
    const payload: string = this.tagDetail.id.toString()
    try {
      this.loading = true
      await this.deleteSelectedTag(payload)
      this.$emit('delete')
      this.loading = false

    }catch (e) {
      this.snackBar = true
      this.snackBarMessage = this.$t('settingsPage.snackbarErrorMessage').toString()
      this.snackBarColor = 'error'
    }finally {
    }
  }

  getIcon (item: string):string | null {
    if(item && item.length > 4) {
      return item  ? `fas ${item}` : null
    } else {
      return ''
    }
  }

  mounted() {
    if(this.tagDetail && this.tagDetail.id) {
      this.title = this.tagDetail.title
      this.iconClass = this.tagDetail.iconClass
    } else {
      this.title = ''
      this.iconClass = ''
    }
  }
}

</script>

<style lang="stylus" scoped>

</style>
